import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BrandMark } from '@/components/Logo';
import SectionIcon from '@/components/SectionIcon';
import Avatar from '@/components/Avatar';
import SidebarSearch from '@/components/SidebarSearch';
import AccountPanel from '@/components/AccountPanel';
import { I } from '@/lib/icons';
import { SECTIONS } from '@/lib/sections';
import { useWorkspace, useClients, useInvoices, useExpenses } from '@/hooks/useData';
import { isOverdue } from '@/lib/format';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace } = useWorkspace();
  const { invoices } = useInvoices(workspace?.id);
  const { expenses } = useExpenses(workspace?.id);
  const { clients } = useClients(workspace?.id);

  const unpaid = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled');
  const overdueCount = invoices.filter(i => isOverdue(i)).length;
  const paidCount = invoices.filter(i => i.status === 'paid').length;

  const tiles = [
    { id: 'unpaid',  label: 'Nezaplatené',   count: unpaid.length, color: 'red',    icon: 'doc',   path: '/faktury' },
    { id: 'all',     label: 'Všetky FA',     count: invoices.length, color: 'blue', icon: 'gauge', path: '/faktury' },
    { id: 'overdue', label: 'Po splatnosti', count: overdueCount, color: 'orange', icon: 'percent', path: '/faktury' },
    { id: 'paid',    label: 'Zaplatené',     count: paidCount,    color: 'green',  icon: 'check', path: '/faktury' },
  ];

  const sectionItems = SECTIONS.map(s => {
    let count = null;
    if (s.id === 'faktury') count = invoices.length;
    else if (s.id === 'vydavky') count = expenses.length;
    else if (s.id === 'klienti') count = clients.length;
    return { ...s, count };
  });

  const pinned = clients.filter(c => c.is_pinned);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <aside style={{
      width: 260, background: 'var(--bg-alt)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      {/* Brand header */}
      <div style={{ padding: '14px 12px 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <BrandMark size={28} />
        <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>podnikai.sk</div>
      </div>

      {/* Search */}
      <div style={{ padding: '0 8px 10px' }}>
        <SidebarSearch />
      </div>

      {/* Tiles */}
      <div style={{ padding: '0 12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {tiles.map(t => (
          <button key={t.id} data-testid={`sidebar-tile-${t.id}`} onClick={() => navigate(t.path)}
            style={{
              background: `var(--sec-${t.color}-soft)`,
              border: 'none', borderRadius: 'var(--r-md)', padding: '10px 11px',
              display: 'flex', flexDirection: 'column',
              alignItems: 'flex-start', gap: 12, cursor: 'pointer', textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <SectionIcon color={t.color} size={22}>
                {React.createElement(I[t.icon], { width: 13, height: 13 })}
              </SectionIcon>
              <span style={{
                fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em',
                color: `var(--sec-${t.color})`, fontFamily: 'var(--font-display)', lineHeight: 1,
              }}>{t.count}</span>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: `var(--sec-${t.color})` }}>{t.label}</div>
          </button>
        ))}
      </div>

      {/* Section list */}
      <div className="cd-scroll" style={{ padding: '4px 8px 8px', flex: 1, overflowY: 'auto' }}>
        <div style={{
          fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
          padding: '4px 10px 6px', letterSpacing: '0.02em',
        }}>Moje sekcie</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sectionItems.map(s => {
            const active = isActive(s.path);
            return (
              <button key={s.id} data-testid={`sidebar-${s.id}`} onClick={() => navigate(s.path)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                  border: 'none', textAlign: 'left',
                  background: active ? 'var(--bg-selected)' : 'transparent',
                  color: active ? 'var(--product-primary-hover)' : 'var(--text-primary)',
                  borderRadius: 'var(--r-sm)', cursor: 'pointer',
                  fontWeight: active ? 600 : 500, fontFamily: 'inherit',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <SectionIcon color={s.color} size={22}>
                  {React.createElement(I[s.icon], { width: 13, height: 13 })}
                </SectionIcon>
                <span style={{ fontSize: 13, flex: 1 }}>{s.label}</span>
                {s.count != null && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {s.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Pinned clients */}
        {pinned.length > 0 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
              padding: '16px 10px 6px', letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <I.pin /> Pripnutí klienti
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pinned.map(c => (
                <button key={c.id} onClick={() => navigate('/klienti')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '5px 10px', border: 'none', background: 'transparent',
                    borderRadius: 'var(--r-sm)', cursor: 'pointer', textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Avatar name={c.name} size={20} />
                  <span style={{
                    fontSize: 12.5, color: 'var(--text-primary)', flex: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{c.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Account footer panel */}
      <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
        <AccountPanel />
      </div>
    </aside>
  );
}
